import { useState } from 'react';

import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Textarea } from '@/new/components/shadcn/textarea';
import { cn } from '@/new/lib/utils';

interface InputField {
  currentValue: string;
  label: string;
  placeholder?: string;
  type?: 'input' | 'textarea';
}

interface SingleFieldProps {
  currentValue: string;
  fieldLabel: string;
  placeholder?: string;
  fieldType?: 'input' | 'textarea';
  onSave: (value: string) => void;
  inputs?: never;
}

interface MultiFieldProps {
  inputs: InputField[];
  onSave: (values: Record<string, string>) => void;
  currentValue?: never;
  fieldLabel?: never;
  placeholder?: never;
}

type EditFieldDialogProps = {
  title: string;
  description: string;
  triggerClassName?: string;
} & (SingleFieldProps | MultiFieldProps);

/**
 * A dialog with a pencil icon trigger for editing one or more text fields.
 *
 * @example Single field mode
 * ```tsx
 * <EditFieldDialog
 *   title="Edit Name"
 *   description="Update the name."
 *   fieldLabel="Name"
 *   fieldType="textarea" // optional, defaults to 'input'
 *   currentValue={name}
 *   placeholder="Enter name..."
 *   onSave={(value) => updateName(value)}
 * />
 * ```
 *
 * @example Multi-field mode
 * ```tsx
 * <EditFieldDialog
 *   title="Edit Details"
 *   description="Update the details."
 *   inputs={[
 *     { label: 'First Name', currentValue: firstName, placeholder: 'John' },
 *     { label: 'Bio', currentValue: bio, placeholder: 'About you...', type: 'textarea' },
 *   ]}
 *   onSave={(values) => {
 *     // values = { 'First Name': '...', 'Bio': '...' }
 *   }}
 * />
 * ```
 */
export function EditFieldDialog(props: EditFieldDialogProps) {
  const { title, description, triggerClassName } = props;

  const isMultiField = 'inputs' in props && props.inputs !== undefined;

  const initialValues = isMultiField
    ? Object.fromEntries(props.inputs.map((input) => [input.label, input.currentValue]))
    : { single: props.currentValue };

  const [open, setOpen] = useState(false);
  const [editedValues, setEditedValues] = useState<Record<string, string>>(initialValues);

  const handleSave = () => {
    if (isMultiField) {
      props.onSave(editedValues);
    } else {
      props.onSave(editedValues.single);
    }
    setOpen(false);
  };

  const hasChanges = isMultiField
    ? props.inputs.some((input) => editedValues[input.label] !== input.currentValue)
    : editedValues.single !== props.currentValue;

  const fields = isMultiField
    ? props.inputs
    : [{ currentValue: props.currentValue, label: props.fieldLabel, placeholder: props.placeholder, type: props.fieldType }];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" className={cn('px-1 py-1 text-gray-400 hover:text-gray-800', triggerClassName)}>
          <Icon className="lucide--pencil" />
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {fields.map((field, index) => {
          const key = isMultiField ? field.label : 'single';
          const InputComponent = field.type === 'textarea' ? Textarea : Input;
          return (
            <div key={field.label} className={index > 0 ? 'mt-4' : ''}>
              <Label htmlFor={`edit-field-input-${field.label}`}>{field.label}</Label>
              <InputComponent
                className="text-gray-800"
                id={`edit-field-input-${field.label}`}
                value={editedValues[key]}
                onChange={(e) => setEditedValues((prev) => ({ ...prev, [key]: e.target.value }))}
                placeholder={field.placeholder}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && field.type !== 'textarea') {
                    handleSave();
                  }
                }}
              />
            </div>
          );
        })}

        <DialogFooter>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={!hasChanges}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
